/**
 * The feature flags this app reads, and what it does until it can read them.
 *
 * These are read from LaunchDarkly, by the same keys and for the same purpose as
 * the legacy admin, targeted at individual people by email. The defaults below
 * are what the portal answers when LaunchDarkly cannot: production until its
 * build variable is set, a blocked or slow client, or a flag LaunchDarkly does
 * not recognise.
 *
 * Flag names are the LaunchDarkly keys verbatim. They are the names someone with
 * LaunchDarkly access can search for, which is the whole point of naming them on
 * screen, so do not tidy them into something more readable.
 */

import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';

export interface FeatureFlags {
  /**
   * Whether the settings a distributor's integration was set up around are
   * protected at all. Off means the portal behaves as it did before the guard
   * existed: every setting editable on its own tab by anyone.
   *
   * Defaults on, because the guard is the reason this code exists and a missing
   * flag must not remove it. Switch it off in LaunchDarkly, not here.
   */
  integratedSupplierMarkingUserFlow: boolean;
  /**
   * Whether the person looking may change those settings.
   *
   * Defaults off. If LaunchDarkly cannot answer, nobody edits these settings,
   * which is the safe half: defaulting on would open them to everyone, and that
   * is the state that broke live integrations in the first place.
   */
  adminCanEditSupplierIntegrationSettings: boolean;
  /**
   * Whether a distributor's access package is shown at all.
   *
   * Defaults off. The package is commercial information, and the legacy admin hid
   * the whole row behind this rather than showing it to everyone, so a flag
   * LaunchDarkly cannot answer must not reveal it.
   */
  viewAccessPackage: boolean;
  /**
   * Whether the person looking may change the package.
   *
   * Defaults off, and separate from viewing on purpose: legacy gated the row and
   * the Change link with two different flags, so seeing a distributor's package is
   * not the same right as moving it between tiers.
   */
  adminAccessPackageRights: boolean;
}

export const FEATURE_FLAG_DEFAULTS: FeatureFlags = {
  integratedSupplierMarkingUserFlow: true,
  adminCanEditSupplierIntegrationSettings: false,
  viewAccessPackage: false,
  adminAccessPackageRights: false,
};

/** Every flag name this app reads, for the copy that has to name one. */
export const FLAG_NAMES = Object.keys(FEATURE_FLAG_DEFAULTS) as ReadonlyArray<
  keyof FeatureFlags
>;

/**
 * The flags for the signed-in person, from LaunchDarkly where it can answer and
 * from the defaults above where it cannot.
 *
 * The type guard is the important part. LaunchDarkly returns nothing for a flag
 * it does not know, and `undefined` is falsy, so reading it raw would turn the
 * guard off the moment a flag were renamed or archived. Only a real boolean
 * overrides a default.
 */
export const useFeatureFlags = (): FeatureFlags => {
  const ldFlags = useLDFlags() as Record<string, unknown> | undefined;

  const resolve = (name: keyof FeatureFlags): boolean => {
    const value = ldFlags?.[name];
    return typeof value === 'boolean' ? value : FEATURE_FLAG_DEFAULTS[name];
  };

  return {
    integratedSupplierMarkingUserFlow: resolve(
      'integratedSupplierMarkingUserFlow'
    ),
    adminCanEditSupplierIntegrationSettings: resolve(
      'adminCanEditSupplierIntegrationSettings'
    ),
    viewAccessPackage: resolve('viewAccessPackage'),
    adminAccessPackageRights: resolve('adminAccessPackageRights'),
  };
};

/** One flag, for a caller that only needs one. */
export const useFeatureFlag = (name: keyof FeatureFlags): boolean =>
  useFeatureFlags()[name];
